import React, {useState} from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	CircularProgress,
	Typography,
	useMediaQuery,
	Paper,
	Fade,
	Tooltip,
	IconButton,
} from "@material-ui/core";
import {
	FileCopy as CopyIcon,
	Check as CheckIcon,
} from "@material-ui/icons";
import {makeStyles, useTheme} from "@material-ui/core/styles";
import {CurrencyFormat} from "../../util/currencyFormat";

const PRIMARY_COLOR = "#9cc54b";

const useStyles = makeStyles((theme) => ({
	dialogPaper: {
		borderRadius: 16,
		overflow: "hidden",
		background: "#fdfdfd",
		boxShadow: "0px 10px 40px rgba(0,0,0,0.15)",
	},
	header: {
		fontWeight: 700,
		fontSize: "1.4rem",
		letterSpacing: "0.3px",
		padding: theme.spacing(3, 3, 2),
		borderBottom: "1px solid #e0e0e0",
		background: "#fff",
	},
	content: {
		padding: theme.spacing(3),
		backgroundColor: "#fafafa",
	},
	tableWrapper: {
		borderRadius: 12,
		overflow: "hidden",
		background: "#fff",
		boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
	},
	tableHead: {
		backgroundColor: "#f5f6f5",
	},
	headCell: {
		fontWeight: 600,
		fontSize: "0.95rem",
		color: "#333",
		textTransform: "uppercase",
		borderBottom: "2px solid #e0e0e0",
	},
	clickableRow: {
		cursor: "pointer",
		transition: "all 0.2s ease",
		"&:hover": {
			backgroundColor: "#f9fdf5",
		},
	},
	selectedRow: {
		background: "linear-gradient(90deg, #eaf6d8 0%, #f9fff1 100%)",
		boxShadow: "inset 0 0 0 2px #b2d67b",
		"& td": {
			color: "#3e5f20",
			fontWeight: 600,
		},
	},
	tableCell: {
		fontSize: "0.95rem",
		borderBottom: "1px solid #eee",
		color: "#444",
	},
	actions: {
		justifyContent: "space-between",
		padding: theme.spacing(2.5, 3),
		backgroundColor: "#fff",
		borderTop: "1px solid #e0e0e0",
	},
	primaryButton: {
		backgroundColor: PRIMARY_COLOR,
		color: "#fff",
		padding: theme.spacing(1, 4),
		borderRadius: 8,
		fontWeight: 600,
		fontSize: "0.95rem",
		boxShadow: "0 3px 10px rgba(156,197,75,0.4)",
		transition: "all 0.3s ease",
		"&:hover": {
			backgroundColor: "#89ad44",
			transform: "translateY(-1px)",
			boxShadow: "0 4px 12px rgba(156,197,75,0.5)",
		},
	},
	cancelButton: {
		borderColor: "#ccc",
		color: "#666",
		fontWeight: 500,
		borderRadius: 8,
		padding: theme.spacing(1, 3),
		"&:hover": {
			backgroundColor: "#f9f9f9",
		},
	},
	loadingContainer: {
		textAlign: "center",
		padding: theme.spacing(6),
	},
	emptyText: {
		textAlign: "center",
		color: "#777",
		margin: theme.spacing(4, 0),
		fontSize: "1rem",
	},
}));

const GenerateCouponDialog = ({
	                              open,
	                              onClose,
	                              couponTemplateList = [],
	                              couponFetching,
	                              onSelect,
	                              selectedCoupon,
	                              onCreate,
	                              isRequesting,
                              }) => {
	const classes = useStyles();
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const [copiedId, setCopiedId] = useState(null);

	const handleCopy = async (coupon) => {
		try {
			await navigator.clipboard.writeText(coupon.code);
			setCopiedId(coupon._id);
			setTimeout(() => setCopiedId(null), 1500);
		} catch (err) {
			console.error("Failed to copy code:", err);
		}
	};
	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="md"
			fullScreen={fullScreen}
			aria-labelledby="generate-coupon-dialog"
			classes={{paper: classes.dialogPaper}}
			TransitionComponent={Fade}
			TransitionProps={{timeout: 400}}
			style={{zIndex: 1}}
		>
			<DialogTitle disableTypography className={classes.header}>
				<Typography variant="h6">Generate New Coupon</Typography>
			</DialogTitle>

			<DialogContent dividers className={classes.content}>
				{couponFetching ? (
					<div className={classes.loadingContainer}>
						<CircularProgress size={30} style={{color: PRIMARY_COLOR}}/>
						<Typography
							variant="body2"
							style={{marginTop: 12, color: "#555"}}
						>
							Loading templates...
						</Typography>
					</div>
				) : couponTemplateList.length > 0 ? (
					<Paper elevation={0} className={classes.tableWrapper}>
						<Table>
							<TableHead className={classes.tableHead}>
								<TableRow>
									<TableCell className={classes.headCell}>Name</TableCell>
									<TableCell className={classes.headCell}>Code</TableCell>
									<TableCell className={classes.headCell}>Valid Date</TableCell>
									<TableCell className={classes.headCell}>Discount</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{couponTemplateList.map((coupon, idx) => {
									const isSelected = selectedCoupon?._id === coupon._id;
									const isCopied = copiedId === coupon._id;
									return (
										<TableRow
											key={idx}
											hover
											onClick={() => onSelect(coupon)}
											className={`${classes.clickableRow} ${
												isSelected ? classes.selectedRow : ""
											}`}
										>
											<TableCell className={classes.tableCell}>
												{coupon?.name || "undefined"}
											</TableCell>
											<TableCell className={classes.tableCell}>
												<div className={classes.codeCell}>
													{coupon?.code || "undefined"}
													<Tooltip title={isCopied ? "Copied!" : "Copy Code"} placement="top">
														<IconButton
															size="small"
															onClick={(e) => {
																e.stopPropagation(); // prevent row selection
																handleCopy(coupon);
															}}
														>
															{isCopied ? (
																<CheckIcon fontSize="small" className={classes.copiedIcon}/>
															) : (
																<CopyIcon fontSize="small" className={classes.copyIcon}/>
															)}
														</IconButton>
													</Tooltip>
												</div>
											</TableCell>
											<TableCell className={classes.tableCell}>
												{`${coupon?.valid_date} (${coupon?.validity_type})`}
											</TableCell>
											<TableCell className={classes.tableCell}>
												{coupon?.discountPercentage
													? `${coupon.discountPercentage} %`
													: coupon?.discountFixedPrice
														? CurrencyFormat(coupon.discountFixedPrice)
														: "-"}
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</Paper>
				) : (
					<Typography className={classes.emptyText}>
						No coupons found
					</Typography>
				)}
			</DialogContent>

			<DialogActions className={classes.actions}>
				<Button
					className={classes.cancelButton}
					variant="outlined"
					onClick={onClose}
					disabled={isRequesting}
				>
					Cancel
				</Button>
				<Button
					className={classes.primaryButton}
					variant="contained"
					disabled={isRequesting}
					onClick={onCreate}
				>
					{isRequesting ? (
						<CircularProgress size={22} style={{color: "#fff"}}/>
					) : (
						"Create"
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default GenerateCouponDialog;
